import {
  Box,
  CircularProgress,
  IconButton,
  TextField,
  Typography,
  Avatar,
} from "@mui/material";
import {
  ArrowForward as BackIcon,
  Delete as DeleteIcon,
  OpenInNew as LinkIcon,
  CloudUpload as UploadIcon,
  Medication as SupplementIcon,
} from "@mui/icons-material";
import { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getSupplementById, createSupplement, updateSupplement, deleteSupplement } from "../services/SupplementService";
import { uploadImage } from "../services/MediaUploadService";
import { useMessageContext } from "../contexts/MessageContext";
import { useCompany } from "../contexts/CompanyContext";
import { RTL } from "../cache/CacheRtl";
import { ConfirmDialog } from "../componenets/ui";
import { t } from "../componenets/dashboard/coach/coachTokens";

/* Coach-styled text field */
const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: t.rs,
    backgroundColor: t.white,
    fontSize: "14px",
    "& .MuiOutlinedInput-notchedOutline": { borderColor: t.border },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: t.green },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: t.green, borderWidth: "2px" },
  },
  "& .MuiInputLabel-root": { fontSize: "14px", color: t.t2, "&.Mui-focused": { color: t.green } },
};

const SupplementPage = () => {
  const { supplementId } = useParams();
  const navigate = useNavigate();
  const { notificationHandler } = useMessageContext();
  const { company } = useCompany();
  const isNew = supplementId === "new";
  const fileInputRef = useRef(null);

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const [form, setForm] = useState({
    name: "",
    description: "",
    imageUrl: "",
    purchaseLink: "",
  });

  useEffect(() => {
    if (!isNew) {
      getSupplementById(supplementId).then(res => {
        if (res.success && res.data) {
          setForm({
            name: res.data.name || "",
            description: res.data.description || "",
            imageUrl: res.data.imageUrl || "",
            purchaseLink: res.data.purchaseLink || "",
          });
        } else {
          notificationHandler.error("שגיאה בטעינת התוסף");
          navigate("/app/supplements");
        }
        setLoading(false);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supplementId]);

  const handleChange = (field) => (e) => {
    setForm(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const cdnUrl = await uploadImage(file);
      if (cdnUrl) {
        setForm(prev => ({ ...prev, imageUrl: cdnUrl }));
        notificationHandler.success("התמונה הועלתה בהצלחה");
      }
    } catch (error) {
      notificationHandler.error("שגיאה בהעלאת התמונה");
    }
    setUploading(false);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      notificationHandler.error("יש להזין שם תוסף");
      return;
    }

    setSaving(true);
    const data = {
      name: form.name.trim(),
      description: form.description.trim(),
      imageUrl: form.imageUrl.trim(),
      purchaseLink: form.purchaseLink.trim(),
    };

    let res;
    if (isNew) {
      const payload = company?.id ? { ...data, companyId: company.id } : data;
      res = await createSupplement(payload);
    } else {
      res = await updateSupplement(supplementId, data);
    }

    if (res.success) {
      notificationHandler.success(res.message);
      if (isNew && res.data?.id) {
        navigate(`/app/supplements/${res.data.id}`, { replace: true });
      }
    } else {
      notificationHandler.error(res.message);
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    const res = await deleteSupplement(supplementId);
    if (res.success) {
      notificationHandler.success(res.message);
      navigate("/app/supplements");
    } else {
      notificationHandler.error(res.message);
    }
    setDeleteOpen(false);
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 400 }}>
        <CircularProgress size={32} sx={{ color: t.green }} />
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "18px" }}>
      {/* Delete confirmation */}
      <ConfirmDialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={handleDelete}
        title="מחיקת תוסף"
        description={`האם למחוק את התוסף "${form.name}"?`}
        variant="danger"
        confirmText="מחק"
      />

      {/* ─── Back + title (on canvas) ──────────────────── */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, direction: "rtl" }}>
        <IconButton
          onClick={() => navigate("/app/supplements")}
          sx={{ color: t.t2, p: 1, "&:hover": { bgcolor: t.greenLight, color: t.green } }}
        >
          <BackIcon />
        </IconButton>
        <Typography sx={{ fontSize: { xs: "22px", md: "26px" }, fontWeight: 800, color: t.text, lineHeight: 1.2, letterSpacing: "-0.3px" }}>
          {isNew ? "תוסף חדש" : (form.name || "תוסף")}
        </Typography>
      </Box>

      {/* ─── Card ──────────────────────────────────────── */}
      <Box sx={{
        background: t.white, border: `1px solid ${t.border}`, borderRadius: t.r,
        boxShadow: t.sh, overflow: "hidden", direction: "rtl",
      }}>
        {/* Gradient header band */}
        <Box sx={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: { xs: "14px 16px", md: "16px 22px" },
          borderBottom: `1px solid ${t.border}`,
          background: `linear-gradient(135deg, ${t.greenLight}, #f4fbf8)`,
        }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
            <Box sx={{
              width: 40, height: 40, borderRadius: "11px", background: t.white,
              border: `1px solid ${t.border}`, display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <SupplementIcon sx={{ color: t.green, fontSize: 22 }} />
            </Box>
            <Box>
              <Typography sx={{ fontSize: 16, fontWeight: 800, color: t.text, letterSpacing: "-0.2px" }}>
                {isNew ? "תוסף חדש" : "פרטי תוסף"}
              </Typography>
              <Typography sx={{ fontSize: 12, fontWeight: 500, color: t.t2 }}>
                {isNew ? "הוספת תוסף חדש למאגר" : "עריכת פרטי התוסף"}
              </Typography>
            </Box>
          </Box>
          {!isNew && (
            <Box
              component="button"
              onClick={() => setDeleteOpen(true)}
              sx={{
                display: "inline-flex", alignItems: "center", gap: "5px",
                background: t.white, color: t.red,
                height: 32, px: "12px", borderRadius: t.rs,
                fontSize: "12.5px", fontWeight: 600,
                border: `1px solid ${t.red}33`, cursor: "pointer", fontFamily: "inherit",
                transition: "all .14s",
                "&:hover": { background: t.red, color: "#fff", borderColor: t.red },
              }}
            >
              <DeleteIcon sx={{ fontSize: 16 }} />
              מחק תוסף
            </Box>
          )}
        </Box>

        {/* Body — image on the side, fields fill the rest (desktop) */}
        <RTL>
          <Box sx={{
            padding: { xs: "20px 16px", md: "28px 28px" },
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "220px 1fr" },
            gap: { xs: 2.5, md: 4 },
            alignItems: "start",
          }}>
            {/* Image upload column */}
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5 }}>
              <Avatar
                src={form.imageUrl}
                sx={{ width: "100%", maxWidth: 180, height: 180, borderRadius: t.r, border: `1px solid ${t.border}`, bgcolor: t.greenLight, color: t.green }}
                variant="rounded"
              >
                <SupplementIcon sx={{ fontSize: 56 }} />
              </Avatar>
              <input ref={fileInputRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleImageUpload} />
              <Box
                component="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                sx={{
                  display: "inline-flex", alignItems: "center", justifyContent: "center", gap: "6px", width: "100%", maxWidth: 180,
                  background: t.white, color: t.green,
                  height: 36, px: "14px", borderRadius: t.rs,
                  fontSize: "13px", fontWeight: 600,
                  border: `1px solid ${t.green}55`, cursor: uploading ? "default" : "pointer", fontFamily: "inherit",
                  transition: "all .14s",
                  "&:hover": uploading ? {} : { background: t.greenLight },
                  "&:disabled": { opacity: 0.6 },
                }}
              >
                {uploading ? <CircularProgress size={15} sx={{ color: t.green }} /> : <UploadIcon sx={{ fontSize: 17 }} />}
                {uploading ? "מעלה..." : "העלאת תמונה"}
              </Box>
              <Typography sx={{ fontSize: 11.5, color: t.t3, textAlign: "center" }}>PNG / JPG · יחס מרובע מומלץ</Typography>
            </Box>

            {/* Fields column */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2.25 }}>
              <TextField fullWidth label="שם התוסף" value={form.name} onChange={handleChange("name")} required sx={fieldSx} />
              <TextField fullWidth label="תיאור" value={form.description} onChange={handleChange("description")} multiline rows={4} sx={fieldSx} />
              <TextField
                fullWidth
                label="קישור לרכישה"
                value={form.purchaseLink}
                onChange={handleChange("purchaseLink")}
                placeholder="https://..."
                sx={fieldSx}
                InputProps={{
                  endAdornment: form.purchaseLink ? (
                    <IconButton size="small" onClick={() => window.open(form.purchaseLink, "_blank")} sx={{ color: t.green }}>
                      <LinkIcon fontSize="small" />
                    </IconButton>
                  ) : null,
                }}
              />
            </Box>
          </Box>
        </RTL>

        {/* Footer */}
        <Box sx={{
          display: "flex", justifyContent: "flex-start",
          padding: { xs: "14px 16px", md: "16px 28px" },
          borderTop: `1px solid ${t.border}`, background: t.bg,
        }}>
          <Box
            component="button"
            onClick={handleSave}
            disabled={saving || !form.name.trim()}
            sx={{
              display: "inline-flex", alignItems: "center", justifyContent: "center", gap: "6px",
              background: t.green, color: "#fff",
              height: 40, px: "26px", borderRadius: t.rs,
              fontSize: "14px", fontWeight: 600,
              border: "none", cursor: "pointer", fontFamily: "inherit",
              transition: "all .14s",
              "&:hover": { background: t.greenDark, transform: "translateY(-1px)", boxShadow: "0 4px 12px rgba(1,187,167,.35)" },
              "&:disabled": { background: t.border, color: t.t3, cursor: "not-allowed", transform: "none", boxShadow: "none" },
            }}
          >
            {saving ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : (isNew ? "צור תוסף" : "שמור שינויים")}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default SupplementPage;
